import { AlertTriangle } from 'lucide-react'

import { StockLevelItem } from '@/hooks/features/stock'

import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'

interface Props {
  items: StockLevelItem[]
  loading: boolean
}

export const LowStockAlerts = ({ items, loading }: Props) => {
  const alerts = items.filter((i) => i.status === 'low-stock' || i.status === 'out-of-stock')

  if (loading) {
    return (
      <div className="divide-y divide-border-subtle">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="px-4 py-3">
            <Skeleton className="mb-3 h-4 w-1/2" />
            <Skeleton className="h-2 w-full" />
          </div>
        ))}
      </div>
    )
  }

  if (alerts.length === 0) {
    return (
      <div className="px-4 py-12 text-center text-muted-foreground">
        <AlertTriangle className="mx-auto mb-2 size-8 text-faint" aria-hidden="true" />
        <p className="text-sm">No low stock alerts</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border-subtle">
      {alerts.map((item) => {
        const pct =
          item.minStockQuantity > 0 ? Math.round((item.quantity / item.minStockQuantity) * 100) : 0
        const isOut = item.quantity <= 0

        return (
          <div key={item._id} className="px-4 py-3">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <AlertTriangle
                  className={`size-4 shrink-0 ${isOut ? 'text-error' : 'text-warning'}`}
                  aria-hidden="true"
                />
                <span className="truncate text-sm font-medium text-ink">{item.productName}</span>
                <span className="text-sm text-muted-foreground">{item.sku}</span>
              </div>
              <Badge tone={isOut ? 'error' : 'warning'}>
                {isOut ? 'Out of stock' : 'Low stock'}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Progress value={pct} className="h-2 flex-1" />
              <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                {item.quantity} / {item.minStockQuantity}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
